import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { careGapAPI, aiAPI } from '../../lib/api';
import { 
  Warning, 
  CheckCircle,
  Robot,
  Sparkle,
  Bell,
  Calendar,
  Syringe,
  FirstAidKit
} from '@phosphor-icons/react';

export default function CareGapsPage() {
  const { user } = useAuth();
  const [careGaps, setCareGaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await careGapAPI.getAll();
      setCareGaps(res.data);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading care gaps:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (gapId, status) => {
    try {
      await careGapAPI.updateStatus(gapId, status);
      loadData();
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error updating care gap:', error);
    }
  };

  const getGapIcon = (type) => {
    switch (type) {
      case 'screening': return Calendar;
      case 'vaccination': return Syringe;
      case 'follow_up': return Bell;
      default: return FirstAidKit;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'low': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const openGaps = careGaps.filter(g => g.status === 'open');
  const addressedGaps = careGaps.filter(g => g.status !== 'open');

  const gapsByPriority = {
    high: openGaps.filter(g => g.priority === 'high'),
    medium: openGaps.filter(g => g.priority === 'medium'),
    low: openGaps.filter(g => g.priority === 'low'),
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Care Gap Notifications
        </h1>
        <p className="text-slate-500 mt-1">
          Monitor and address patient care gaps to improve outcomes
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="kpi-card card-hover border-l-4 border-l-red-500" data-testid="high-priority-gaps">
          <div className="flex items-center justify-between">
            <div>
              <div className="kpi-value text-red-600">{gapsByPriority.high.length}</div>
              <div className="kpi-label">High Priority</div>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <Warning size={24} className="text-red-600" weight="fill" />
            </div>
          </div>
        </div>

        <div className="kpi-card card-hover border-l-4 border-l-amber-500" data-testid="medium-priority-gaps">
          <div className="flex items-center justify-between">
            <div>
              <div className="kpi-value text-amber-600">{gapsByPriority.medium.length}</div>
              <div className="kpi-label">Medium Priority</div>
            </div>
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <Bell size={24} className="text-amber-600" />
            </div>
          </div>
        </div>

        <div className="kpi-card card-hover border-l-4 border-l-green-500" data-testid="low-priority-gaps">
          <div className="flex items-center justify-between">
            <div>
              <div className="kpi-value text-green-600">{gapsByPriority.low.length}</div>
              <div className="kpi-label">Low Priority</div>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle size={24} className="text-green-600" />
            </div>
          </div>
        </div>

        <div className="kpi-card card-hover border-l-4 border-l-teal-500" data-testid="addressed-gaps">
          <div className="flex items-center justify-between">
            <div>
              <div className="kpi-value text-teal-600">{addressedGaps.length}</div>
              <div className="kpi-label">Addressed</div>
            </div>
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
              <CheckCircle size={24} className="text-teal-600" weight="fill" />
            </div>
          </div>
        </div>
      </div>

      {/* Open Care Gaps */}
      <div className="card-clinical mb-6">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <Warning size={18} className="text-amber-500" />
              Open Care Gaps ({openGaps.length})
            </h2>
          </div>
        </div>
        
        {openGaps.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle size={48} className="text-green-300 mx-auto mb-4" />
            <p className="text-slate-500">All care gaps have been addressed!</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {openGaps.map((gap) => {
              const GapIcon = getGapIcon(gap.gap_type);
              return (
                <div key={gap.id} className="p-4 hover:bg-slate-50" data-testid={`care-gap-${gap.id}`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      gap.priority === 'high' ? 'bg-red-100' :
                      gap.priority === 'medium' ? 'bg-amber-100' : 'bg-green-100'
                    }`}>
                      <GapIcon size={20} className={
                        gap.priority === 'high' ? 'text-red-600' :
                        gap.priority === 'medium' ? 'text-amber-600' : 'text-green-600'
                      } />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-medium text-slate-900">{gap.patient_name}</div>
                          <div className="text-sm text-slate-600 mt-1">{gap.description}</div>
                          <div className="flex items-center gap-3 mt-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${getPriorityColor(gap.priority)}`}>
                              {gap.priority} priority
                            </span>
                            <span className="text-xs text-slate-500 capitalize">
                              {gap.gap_type.replace(/_/g, ' ')}
                            </span>
                            <span className="text-xs text-slate-500">
                              {new Date(gap.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleUpdateStatus(gap.id, 'addressed')}
                            className="text-teal-600 hover:text-teal-700 text-sm font-medium"
                            data-testid={`address-gap-${gap.id}`}
                          >
                            Mark Addressed
                          </button>
                          <button
                            onClick={() => handleUpdateStatus(gap.id, 'dismissed')}
                            className="text-slate-400 hover:text-slate-600 text-sm"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Addressed Care Gaps */}
      {addressedGaps.length > 0 && (
        <div className="card-clinical">
          <div className="p-6 border-b border-slate-200">
            <h2 className="font-semibold text-slate-900">Recently Addressed</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {addressedGaps.slice(0, 5).map((gap) => (
              <div key={gap.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={18} className="text-green-500" />
                    <div>
                      <span className="font-medium text-slate-700">{gap.patient_name}</span>
                      <span className="text-slate-500 mx-2">-</span>
                      <span className="text-sm text-slate-600">{gap.description}</span>
                    </div>
                  </div>
                  <span className="text-xs text-slate-500">
                    {gap.resolved_at ? new Date(gap.resolved_at).toLocaleDateString() : 'Addressed'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
